import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { BarChart, TrendingUp, Download, DollarSign, Users, Calendar, BedDouble } from 'lucide-react';
import { PmsKpiCard } from '@/components/pms/PmsKpiCard';
import { toast } from 'sonner';
import { BACKEND_URL } from '@/lib/backendUrl';

export
function ReportsTab({ hotelId }) {
  const [loading, setLoading] = useState(true);
  const [reports, setReports] = useState(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  useEffect(() => {
    const today = new Date();
    const thirtyDaysAgo = new Date(today);
    thirtyDaysAgo.setDate(today.getDate() - 30);
    
    setEndDate(today.toISOString().split('T')[0]);
    setStartDate(thirtyDaysAgo.toISOString().split('T')[0]);
    
    loadReports();
  }, [hotelId]);

  const loadReports = async (customStart, customEnd) => {
    try {
      setLoading(true);
      const backendUrl = BACKEND_URL;
      
      let url = `${backendUrl}/api/pms/${hotelId}/reports`;
      if (customStart && customEnd) {
        url += `?start_date=${customStart}&end_date=${customEnd}`;
      }
      
      const response = await fetch(url);
      const data = await response.json();
      if (data.success) {
        setReports(data);
      }
    } catch (error) {
      console.error('Failed to load reports:', error);
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  const handleDateChange = () => {
    if (startDate && endDate) {
      loadReports(startDate, endDate);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <Skeleton className="h-12 w-12 rounded-full mx-auto mb-4" />
        <Skeleton className="h-4 w-48 mx-auto" />
      </div>
    );
  }

  if (!reports) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <p className="text-muted-foreground">Unable to load reports</p>
        </CardContent>
      </Card>
    );
  }

  const { current_status, bookings, revenue, occupancy, period } = reports;

  return (
    <div className="space-y-6" data-testid="reports-tab">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold heading-font mb-2">Reports & Analytics</h2>
          <p className="text-muted-foreground">Financial reports, occupancy trends, and performance metrics</p>
        </div>
        
        <div className="flex items-center gap-2">
          <Input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="w-auto"
            data-testid="reports-start-date"
          />
          <span className="text-muted-foreground">to</span>
          <Input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="w-auto"
            data-testid="reports-end-date"
          />
          <Button onClick={handleDateChange} size="sm" data-testid="reports-apply-dates">
            Apply
          </Button>
        </div>
      </div>

      <div className="text-sm text-muted-foreground">
        Showing data from <strong className="mono">{period.start_date}</strong> to <strong className="mono">{period.end_date}</strong>
      </div>

      <div>
        <h3 className="text-lg heading-font mb-4">Key Performance Indicators</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <PmsKpiCard
            title="Occupancy Rate"
            value={`${occupancy.period_occupancy_pct}%`}
            icon={BedDouble}
            variant="primary"
            subtitle={`${occupancy.total_room_nights_sold} nights sold`}
          />
          <PmsKpiCard
            title="ADR"
            value={`$${revenue.adr}`}
            icon={DollarSign}
            variant="success"
            subtitle="Average Daily Rate"
          />
          <PmsKpiCard
            title="RevPAR"
            value={`$${revenue.revpar}`}
            icon={TrendingUp}
            subtitle="Revenue Per Available Room"
          />
          <PmsKpiCard
            title="Total Revenue"
            value={`$${revenue.total_revenue}`}
            icon={DollarSign}
            variant="success"
            subtitle={`${bookings.total} bookings`}
          />
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Revenue Breakdown</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">Total Revenue</p>
              <p className="text-2xl font-bold mono">${revenue.total_revenue}</p>
            </div>
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">Hotel Revenue</p>
              <p className="text-2xl font-bold mono text-[hsl(var(--status-clean))]">${revenue.hotel_revenue}</p>
            </div>
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">Platform Fees</p>
              <p className="text-2xl font-bold mono text-muted-foreground">${revenue.platform_fees}</p>
            </div>
          </div>
          
          <Separator />
          
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground">Average Booking Value</p>
              <p className="text-lg font-semibold mono">${revenue.avg_booking_value}</p>
            </div>
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground">Room Nights Sold</p>
              <p className="text-lg font-semibold">{occupancy.total_room_nights_sold}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Bookings Summary</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-4 bg-muted/50 rounded-lg">
              <p className="text-3xl font-bold">{bookings.total}</p>
              <p className="text-sm text-muted-foreground mt-1">Total Bookings</p>
            </div>
            <div className="text-center p-4 bg-primary/5 rounded-lg">
              <p className="text-3xl font-bold text-primary">{bookings.confirmed}</p>
              <p className="text-sm text-muted-foreground mt-1">Confirmed</p>
            </div>
            <div className="text-center p-4 bg-[hsl(var(--status-clean)/0.1)] rounded-lg">
              <p className="text-3xl font-bold text-[hsl(var(--status-clean))]">{bookings.checked_in}</p>
              <p className="text-sm text-muted-foreground mt-1">Checked In</p>
            </div>
            <div className="text-center p-4 bg-muted/50 rounded-lg">
              <p className="text-3xl font-bold">{bookings.checked_out}</p>
              <p className="text-sm text-muted-foreground mt-1">Checked Out</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Current Property Status</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="text-center p-4 border rounded-lg">
              <p className="text-2xl font-bold">{current_status.total_rooms}</p>
              <p className="text-xs text-muted-foreground mt-1">Total Rooms</p>
            </div>
            <div className="text-center p-4 border rounded-lg">
              <p className="text-2xl font-bold text-[hsl(var(--status-occupied))]">{current_status.inhouse_guests}</p>
              <p className="text-xs text-muted-foreground mt-1">In-House</p>
            </div>
            <div className="text-center p-4 border rounded-lg">
              <p className="text-2xl font-bold text-[hsl(var(--status-clean))]">{current_status.clean_rooms}</p>
              <p className="text-xs text-muted-foreground mt-1">Clean</p>
            </div>
            <div className="text-center p-4 border rounded-lg">
              <p className="text-2xl font-bold text-[hsl(var(--status-dirty))]">{current_status.dirty_rooms}</p>
              <p className="text-xs text-muted-foreground mt-1">Dirty</p>
            </div>
            <div className="text-center p-4 border rounded-lg">
              <p className="text-2xl font-bold text-[hsl(var(--status-maintenance))]">{current_status.maintenance_rooms}</p>
              <p className="text-xs text-muted-foreground mt-1">Maintenance</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
